import type { MyTrainingResponse, TrainingAssignment } from "@lms/shared-types";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchJson } from "../../api/client";
import { useAuth } from "../../auth/AuthProvider";
import { Card, EmptyState, ErrorState, LoadingState, PageHeader, StatusPill } from "../../components/ui";
import { useFormat } from "../../lib/format";
import { durationWords, hoursWord, inOrder, STATUS_WORD } from "../../lib/training";

/**
 * My training (CNT-20, ADR-0019 Slice A): everything assigned to the
 * person signed in, the most pressing first, each card saying what it is,
 * how long it takes, why it is assigned and when it is wanted.
 *
 * Cards rather than a table on purpose. A row of cells is the right shape
 * for a roster somebody scans; a course is a thing you read a paragraph of
 * before deciding to start it, and at 320 px a five-column table of
 * courses is a horizontal scroll (ACC-07).
 */
export function TrainingPage() {
  const { session, config } = useAuth();
  const [state, setState] = useState<{ data?: MyTrainingResponse; loading: boolean; error?: unknown }>({ loading: true });
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!session) return;
    let live = true;
    fetchJson<MyTrainingResponse>(config, session, "/v1/training")
      .then((data) => live && setState({ data, loading: false }))
      .catch((error: unknown) => live && setState({ loading: false, error }));
    return () => {
      live = false;
    };
  }, [config, session, tick]);

  return (
    <>
      <PageHeader title="My training" subtitle="What you have been asked to do, and what you have finished. Each one is taken on the provider's own site; you come back here to say it is done." />
      {state.loading && <LoadingState label="Loading your training…" />}
      {state.error !== undefined && <ErrorState title="Your training could not be loaded." error={state.error} onRetry={reload} />}
      {state.data && state.data.rtiPlans.length > 0 && (
        // CNT-26: the running total — what the ledger holds against what the program's RTI plan asks for. Words, not a bar alone.
        <Card title="Your apprenticeship hours" className="mb-6">
          <ul className="space-y-1 text-sm" data-testid="rti-plans">
            {state.data.rtiPlans.map((p) => (
              <li key={p.programId}>
                <strong>{hoursWord(p.creditedHours)}</strong> of the {hoursWord(p.planHours)} on {p.programName}’s related technical instruction (RTI) plan banked so far.
              </li>
            ))}
          </ul>
          <p className="mt-2 max-w-prose text-sm text-muted">A course banks its hours when your program director checks that you finished it.</p>
        </Card>
      )}
      {state.data && state.data.assignments.length === 0 &&<EmptyState title="Nothing assigned yet" hint="When your program director assigns a course it appears here, with what it is and when it is wanted." />}
      {state.data && state.data.assignments.length > 0 && (
        <ul className="grid gap-4 [&>*]:min-w-0">
          {inOrder(state.data.assignments).map((a) => (
            <li key={a.id}>
              <AssignmentCard assignment={a} />
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

export function AssignmentCard({ assignment: a }: { assignment: TrainingAssignment }) {
  const { date } = useFormat();
  const duration = durationWords(a.item.durationMinutes);
  const status = STATUS_WORD[a.status];
  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-ink">
            <Link to={`/training/${a.id}`}>{a.item.title}</Link>
          </h2>
          <p className="mt-1 text-sm text-muted">{[a.item.provider, duration ? `about ${duration}` : undefined].filter(Boolean).join(" · ")}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Overdue is a word beside the state, never the state in red (the portal rule). */}
          {a.overdue && <StatusPill tone="danger">Late</StatusPill>}
          <StatusPill tone={status.tone}>{status.word}</StatusPill>
        </div>
      </div>
      <p className="mt-3 max-w-prose text-sm">{a.item.summary}</p>
      <p className="mt-2 max-w-prose text-sm text-muted">{a.why}</p>
      {a.dueAt && (
        <p className="mt-2 text-sm">
          <span className="text-muted">{a.status === "completed" ? "Was wanted by" : "Wanted by"}</span> <time dateTime={a.dueAt}>{date(a.dueAt)}</time>
        </p>
      )}
      <p className="mt-4 text-sm">
        <Link to={`/training/${a.id}`}>Open {a.item.title}</Link>
      </p>
    </Card>
  );
}
